import { useState } from 'react'
import { useGoalsCrud } from '../mutations/useGoalsCrud.js'
import { useUIStore } from '../store/uiStore.js'

const TEAM = [
  '—', 'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane', 'TBD',
]

/**
 * AddGoalButton — dashed button at the bottom of a pillar. Click to
 * reveal an inline form for naming a new goal, assigning a DRI, and
 * setting a target/unit.
 *
 * On add, the goals array grows by one and a snapshot lands on the
 * undo stack — so a mistake is just a ⌘Z away.
 */
export default function AddGoalButton({ pillarName, pillarColor }) {
  const currentUser = useUIStore((s) => s.currentUser)
  const { addGoal } = useGoalsCrud()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [dri, setDri] = useState(currentUser || '—')
  const [target, setTarget] = useState('')
  const [unit, setUnit] = useState('')

  const reset = () => {
    setName('')
    setDri(currentUser || '—')
    setTarget('')
    setUnit('')
    setOpen(false)
  }

  const submit = () => {
    if (!name.trim()) return
    addGoal({
      pillarName,
      goal: {
        goal: name.trim(),
        metric: '',
        target: target ? Number(target) : 1,
        current: 0,
        unit: unit || '',
        dri: dri || '—',
        owner: dri || '—',
        update: '',
        subgoals: [],
      },
    })
    reset()
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          marginTop: 12,
          padding: '12px 16px',
          background: 'transparent',
          border: `1px dashed ${pillarColor || 'var(--border-strong)'}80`,
          borderRadius: 10,
          fontSize: 12,
          fontWeight: 600,
          color: pillarColor || 'var(--muted)',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          opacity: 0.75,
          transition: 'opacity 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1'
          e.currentTarget.style.background = (pillarColor || '#6b7280') + '08'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.75'
          e.currentTarget.style.background = 'transparent'
        }}
      >
        + Add goal
      </button>
    )
  }

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        background: '#fff',
        border: `1.5px solid ${pillarColor || 'var(--border)'}`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
          if (e.key === 'Escape') reset()
        }}
        placeholder="Goal name (e.g. 'Launch weekly customer newsletter')"
        style={{
          padding: '8px 10px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 13,
          fontFamily: 'inherit',
          fontWeight: 600,
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 120px' }}>
          <span style={labelStyle}>DRI</span>
          <select
            value={dri}
            onChange={(e) => setDri(e.target.value)}
            style={inputStyle}
          >
            {TEAM.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '0 1 110px' }}>
          <span style={labelStyle}>Target</span>
          <input
            type="text"
            inputMode="numeric"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="1"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '0 1 80px' }}>
          <span style={labelStyle}>Unit</span>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            style={inputStyle}
          >
            <option value="">—</option>
            <option value="$">$</option>
            <option value="%">%</option>
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={reset} style={cancelBtn}>Cancel</button>
        <button
          type="button"
          onClick={submit}
          disabled={!name.trim()}
          style={{
            ...addBtn,
            background: name.trim() ? (pillarColor || 'var(--accent)') : '#e5e7eb',
            color: name.trim() ? '#fff' : '#9ca3af',
            cursor: name.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Add Goal
        </button>
      </div>
    </div>
  )
}

const labelStyle = {
  fontSize: 9,
  fontWeight: 700,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.06,
}

const inputStyle = {
  padding: '7px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  fontFamily: 'inherit',
  background: '#fff',
  outline: 'none',
}

const cancelBtn = {
  padding: '7px 14px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  background: '#fff',
  color: 'var(--muted)',
  cursor: 'pointer',
}

const addBtn = {
  padding: '7px 16px',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
}
